import {
  refresh as refreshSession,
  smsTwoFactor,
  twoFactorVerify,
  signin as verify,
  signout as signoutApi,
} from '@/services/login';
import { getErrorMessageFromResponse } from '@/utils/utils';
import { message } from 'antd';
import { parse } from 'querystring';
import { useCallback, useRef, useState } from 'react';

const getRedirectTarget = () => {
  const urlParams = new URL(window.location.href);
  const params = parse(urlParams.search, '?');
  let { redirect } = params;
  if (redirect) {
    const redirectUrlParams = new URL(redirect, urlParams.origin);
    if (redirectUrlParams.origin === urlParams.origin) {
      redirect = redirect.substr(urlParams.origin.length);
      if (redirect.match(/^\/.*#/)) {
        redirect = redirect.substr(redirect.indexOf('#') + 1);
      }
    }
  }

  return redirect || '/';
};

const shouldUseKnowledgeBankSso = (target) => {
  const targetUrl = new URL(target, window.location.origin);

  return (
    targetUrl.hostname === 'wiki.savvyuni.com.cn' ||
    ['/knowledgebank', '/knowledge-bank'].includes(targetUrl.pathname)
  );
};

const getKnowledgeBankSsoUrl = async (token) => {
  const kbRes = await fetch('/api/auth/kb/url', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!kbRes.ok) {
    throw new Error(`HTTP ${kbRes.status}`);
  }

  const kbData = await kbRes.json();

  return kbData?.url || kbData?.data?.url || '';
};

const redirect = async (token) => {
  const target = getRedirectTarget();

  if (token && shouldUseKnowledgeBankSso(target)) {
    try {
      const kbUrl = await getKnowledgeBankSsoUrl(token);
      if (kbUrl) {
        window.location.href = kbUrl;
        return;
      }
    } catch (error) {
      console.error('Knowledge Bank SSO redirect failed:', error);
    }
  }

  window.location.href = target;
};

export default function useLoginModel() {
  const timer = useRef(0);
  const countdownEndAt = useRef(0);
  const [countdown, setCountdown] = useState();
  const [twoFAdata, setTwoFAdata] = useState({});
  const [verifyMethod, setVerifyMethod] = useState();
  const [modalVisible, setModalVisible] = useState(false);
  //未绑定飞书和微信二维码的用户显示二维码
  const [connectModalVisible, setConnectModalVisible] = useState(false);
  //是否跳过绑定
  const [isSkip, setIsSkip] = useState(false);
  const [qrCode, setQrCode] = useState('');
  //电话号码
  const [phone, setPhone] = useState({
    phonePrefix: '',
    phone: '',
  });

  const [trustDevice, setTrustDevice] = useState(false);

  const onCancel = useCallback(() => {
    setModalVisible(false);
  }, []);

  const startCountdown = () => {
    // 2 min cooldown
    countdownEndAt.current = Date.now() + 2 * 60 * 1000;
    timer.current = setInterval(() => {
      const timeRemaining = Math.floor((countdownEndAt.current - Date.now()) / 1000);
      if (timeRemaining <= 0) {
        clearInterval(timer.current);
        countdownEndAt.current = 0;
        setCountdown(0);
      } else {
        setCountdown(timeRemaining);
      }
    }, 1000);
  };

  const onSuccess = useCallback(
    async (token) => {
      message.success('登录成功！');
      localStorage.setItem('token', token);
      countdownEndAt.current = 0;
      clearInterval(timer.current);
      setCountdown(0);
      onCancel();
      await redirect(token);
    },
    [onCancel],
  );

  const signinViaSMS = async (data) => {
    if (!data.phonePrefix || !data.phone) {
      message.error('请填写区号和手机号');
      return Promise.reject('请填写区号和手机号');
    }
    try {
      const { isLocked, cooldown, sp, requestId } = await smsTwoFactor(data);
      if (isLocked) {
        throw new Error('该账号已被锁定，请联系管理员或HR解锁。');
      }
      if (cooldown) {
        throw new Error('发送验证码太频繁，请2分钟后再试。');
      }
      if (sp && requestId) {
        message.success('成功发送验证码，2分钟内有效。');
        setTwoFAdata({
          sp,
          requestId,
        });
      } else {
        setTwoFAdata({});
        throw new Error('找不到用户。');
      }
    } catch (error) {
      message.error(getErrorMessageFromResponse(error));
    }
  };

  const signin = async (values) => {
    if (countdown > 0) {
      setModalVisible(true);
      return Promise.resolve();
    }
    try {
      const res = await verify({ ...values, trustDevice });
      const {
        token,
        isLocked,
        twoFactor,
        requestId,
        sp,
        verifyMethod,
        cooldown,
        qrCode,
        phone,
        phonePrefix,
      } = res;
      if (isLocked) {
        throw new Error('该账号已被锁定，请联系管理员或HR解锁。');
      }
      if (cooldown) {
        throw new Error('发送验证码太频繁，请2分钟后再试。');
      }

      if (qrCode) {
        setConnectModalVisible(true);
        setQrCode(qrCode);
        setIsSkip(false);
        setPhone({ phonePrefix, phone });
      }

      if (twoFactor) {
        startCountdown();
        setTwoFAdata({ requestId, sp });
        setVerifyMethod(verifyMethod);
        setModalVisible(true);
      } else if (token) {
        await onSuccess(token);
      }
    } catch (error) {
      message.error(getErrorMessageFromResponse(error));
    }
  };

  const twoFactorSignin = async (values) => {
    try {
      const res = await twoFactorVerify({ ...values, verifyMethod, ...twoFAdata, trustDevice });
      const { token } = res;
      if (token) {
        await onSuccess(token);
      } else {
        message.error('登录失败，请检查验证码');
      }
    } catch (error) {
      console.log(error);
      message.error(getErrorMessageFromResponse(error));
    }
  };

  const restoreSession = useCallback(async () => {
    try {
      const refreshed = await refreshSession();
      if (refreshed?.token) {
        await onSuccess(refreshed.token);
        return true;
      }
    } catch (error) {
      // Fall through to the legacy access-token path below.
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }

    try {
      const res = await verify({ token });
      if (res?.token) {
        await onSuccess(res.token);
        return true;
      }
    } catch (error) {
      localStorage.removeItem('token');
    }

    return false;
  }, [onSuccess]);

  const signout = async () => {
    try {
      await signoutApi();
    } catch (e) {
      // signout endpoint may fail if token already expired, that's ok
    }
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  return {
    modalVisible,
    verifyMethod,
    onCancel,
    signin,
    twoFactorSignin,
    countdown,
    signinViaSMS,
    connectModalVisible,
    setConnectModalVisible,
    qrCode,
    isSkip,
    setIsSkip,
    phone,
    setPhone,
    signout,
    trustDevice,
    setTrustDevice,
    restoreSession,
  };
}
